import type {
  CreateCustomFieldInput,
  CustomField,
  CustomFieldObject,
  CustomFieldType,
  PatchCustomFieldInput,
} from '@clockwise/clients/custom-fields';
import {
  CUSTOM_FIELD_OBJECT_LABELS,
  CUSTOM_FIELD_OBJECTS,
  CUSTOM_FIELD_TYPE_LABELS,
  CUSTOM_FIELD_TYPES,
} from '@clockwise/clients/custom-fields';
import { ApiError } from '@clockwise/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { customFieldsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

// Form-internal shape: every object's flags are always present (booleans —
// not optional) so the form controls bind to a guaranteed value. The wire
// format uses the partial `CustomFieldObjectConfigMapSchema` from
// `@clockwise/shared`; this stricter local mirror gives the resolver a
// clean output type without `defaults` widening to optional.
const FormObjectConfigEntry = z.object({
  enabled: z.boolean(),
  required: z.boolean(),
  searchable: z.boolean(),
});
const FormSchema = z.object({
  label: z.string().min(1, 'Required').max(120),
  key: z
    .string()
    .min(1, 'Required')
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, 'lowercase letters, digits, and underscores; starts with a letter'),
  fieldType: z.enum(CUSTOM_FIELD_TYPES),
  choices: z.array(z.string().min(1, 'Required').max(120)),
  objectConfig: z.object({
    user: FormObjectConfigEntry,
    customer: FormObjectConfigEntry,
    sow: FormObjectConfigEntry,
  }),
});
type FormValues = z.infer<typeof FormSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When null the sheet is in create mode. */
  field: CustomField | null;
}

const emptyObjectConfig: FormValues['objectConfig'] = Object.fromEntries(
  CUSTOM_FIELD_OBJECTS.map((h) => [h, { enabled: false, required: false, searchable: false }]),
) as FormValues['objectConfig'];

export function CustomFieldFormSheet({ open, onOpenChange, field }: Props) {
  const isEdit = field !== null;
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      label: '',
      key: '',
      fieldType: 'text',
      choices: [],
      objectConfig: emptyObjectConfig,
    },
  });

  // Hydrate on open / when switching between edit targets.
  useEffect(() => {
    if (!open) return;
    if (field) {
      form.reset({
        label: field.label,
        key: field.key,
        fieldType: field.fieldType,
        choices: field.options?.choices ?? [],
        objectConfig: mergeObjectConfig(field.objectConfig),
      });
    } else {
      form.reset({
        label: '',
        key: '',
        fieldType: 'text',
        choices: [],
        objectConfig: emptyObjectConfig,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, field?.id]);

  const labelValue = form.watch('label');
  const fieldType = form.watch('fieldType');
  const choices = form.watch('choices');
  const objectConfig = form.watch('objectConfig');

  // Auto-suggest key from label on create. RHF tracks per-field dirty state;
  // we set the auto-suggested key with `shouldDirty: false` so the field
  // stays "clean" — and stop overwriting the moment the user types in the
  // Key input (which flips dirty to true).
  const keyDirty = form.formState.dirtyFields.key === true;
  useEffect(() => {
    if (isEdit) return;
    if (keyDirty) return;
    form.setValue('key', slugify(labelValue), { shouldValidate: false, shouldDirty: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelValue, isEdit, keyDirty]);

  const submit = (values: FormValues) => {
    // Cross-field checks live here so the Zod resolver stays a plain ZodObject.
    let valid = true;
    if (needsChoices(values.fieldType)) {
      if (values.choices.length === 0) {
        form.setError('choices', { message: 'Add at least one choice' });
        valid = false;
      } else {
        const seen = new Set<string>();
        values.choices.forEach((c, i) => {
          if (seen.has(c)) {
            form.setError(`choices.${i}` as const, { message: 'Duplicate' });
            valid = false;
          }
          seen.add(c);
        });
      }
    }
    const anyEnabled = CUSTOM_FIELD_OBJECTS.some((h) => values.objectConfig[h]?.enabled);
    if (!anyEnabled) {
      form.setError('objectConfig', { message: 'Enable this field for at least one object' });
      valid = false;
    }
    if (!valid) return;
    mutation.mutate(values);
  };

  const mutation = useMutation({
    mutationFn: async (values: FormValues): Promise<CustomField> => {
      if (isEdit) {
        const body: PatchCustomFieldInput = {
          label: values.label,
          options: needsChoices(values.fieldType) ? { choices: values.choices } : null,
          objectConfig: prune(values.objectConfig),
        };
        const res = await customFieldsApi.patch(field.id, body);
        return res.data;
      }
      const body: CreateCustomFieldInput = {
        label: values.label,
        key: values.key,
        fieldType: values.fieldType,
        options: needsChoices(values.fieldType) ? { choices: values.choices } : null,
        objectConfig: prune(values.objectConfig),
      };
      const res = await customFieldsApi.create(body);
      return res.data;
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Custom field updated' : 'Custom field created');
      queryClient.invalidateQueries({ queryKey: queryKeys.customFields.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      onOpenChange(false);
    },
    onError: (err) => {
      // Map server field errors to RHF so they land inline.
      if (err instanceof ApiError && err.fields) {
        for (const fe of err.fields) {
          const path = fe.path as keyof FormValues;
          form.setError(path, { message: fe.message });
        }
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
  });

  const showChoices = needsChoices(fieldType);

  const addChoice = () => {
    form.setValue('choices', [...choices, ''], { shouldDirty: true });
  };
  const updateChoice = (i: number, value: string) => {
    const next = [...choices];
    next[i] = value;
    form.setValue('choices', next, { shouldDirty: true });
  };
  const removeChoice = (i: number) => {
    form.setValue(
      'choices',
      choices.filter((_, j) => j !== i),
      { shouldDirty: true },
    );
  };

  const enabledObjects = useMemo(
    () => CUSTOM_FIELD_OBJECTS.filter((h) => objectConfig[h]?.enabled),
    [objectConfig],
  );

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: mutation.isPending,
    onOpenChange,
  });

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
          <SheetHeader className="border-b px-6 py-4">
            <SheetTitle>{isEdit ? 'Edit custom field' : 'New custom field'}</SheetTitle>
            <SheetDescription>
              {isEdit
                ? 'Update label, choices, or per-object settings. Key and field type are locked once created.'
                : 'Capture tenant-defined data on users, customers, or engagements.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={form.handleSubmit(submit)} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
              {/* Label */}
              <div className="grid gap-1.5">
                <Label htmlFor="cf-label">Label</Label>
                <Input
                  id="cf-label"
                  placeholder="e.g. Project Budget"
                  {...form.register('label')}
                  aria-invalid={!!form.formState.errors.label}
                />
                {form.formState.errors.label ? (
                  <p className="text-xs text-destructive">{form.formState.errors.label.message}</p>
                ) : null}
              </div>

              {/* Key */}
              <div className="grid gap-1.5">
                <Label htmlFor="cf-key">Key</Label>
                <Input
                  id="cf-key"
                  placeholder="e.g. project_budget"
                  disabled={isEdit}
                  {...form.register('key')}
                  aria-invalid={!!form.formState.errors.key}
                />
                <p className="text-xs text-muted-foreground">
                  {isEdit
                    ? 'Locked after create — archive + recreate to change it.'
                    : 'Auto-suggested from the label. Used in audit logs.'}
                </p>
                {form.formState.errors.key ? (
                  <p className="text-xs text-destructive">{form.formState.errors.key.message}</p>
                ) : null}
              </div>

              {/* Field type */}
              <div className="grid gap-1.5">
                <Label htmlFor="cf-type">Type</Label>
                <Select
                  value={fieldType}
                  onValueChange={(v) =>
                    form.setValue('fieldType', v as CustomFieldType, { shouldDirty: true })
                  }
                  disabled={isEdit}
                >
                  <SelectTrigger id="cf-type" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CUSTOM_FIELD_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {CUSTOM_FIELD_TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isEdit ? (
                  <p className="text-xs text-muted-foreground">
                    Locked after create — changing types would invalidate existing values.
                  </p>
                ) : null}
              </div>

              {/* Choices */}
              {showChoices ? (
                <div className="grid gap-1.5">
                  <Label>Choices</Label>
                  <div className="space-y-2">
                    {choices.map((c, i) => (
                      <div key={`choice-${i}`} className="flex items-center gap-2">
                        <Input
                          placeholder={`Choice ${i + 1}`}
                          value={c}
                          onChange={(e) => updateChoice(i, e.target.value)}
                          aria-invalid={!!form.formState.errors.choices?.[i]}
                        />
                        <Button
                          type="button"
                          size="icon-sm"
                          variant="ghost"
                          aria-label="Remove choice"
                          onClick={() => removeChoice(i)}
                        >
                          <X />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" onClick={addChoice}>
                      <Plus /> Add choice
                    </Button>
                  </div>
                  {form.formState.errors.choices &&
                  !Array.isArray(form.formState.errors.choices) ? (
                    <p className="text-xs text-destructive">
                      {form.formState.errors.choices.message}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {/* Objects */}
              <div className="grid gap-2">
                <Label>Available on</Label>
                <div className="space-y-3 rounded-md border p-3">
                  {CUSTOM_FIELD_OBJECTS.map((h) => (
                    <ObjectRow
                      key={h}
                      object={h}
                      cfg={objectConfig[h]}
                      onChange={(next) =>
                        form.setValue(
                          'objectConfig',
                          { ...objectConfig, [h]: next },
                          { shouldDirty: true },
                        )
                      }
                    />
                  ))}
                </div>
                {form.formState.errors.objectConfig &&
                !('user' in (form.formState.errors.objectConfig as object)) ? (
                  <p className="text-xs text-destructive">
                    {(form.formState.errors.objectConfig as { message?: string }).message}
                  </p>
                ) : null}
                {enabledObjects.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Enable at least one object so this field appears somewhere.
                  </p>
                ) : null}
              </div>
            </div>

            <SheetFooter className="flex-row justify-end gap-2 border-t px-6 py-4">
              <Button type="button" variant="ghost" onClick={() => requestOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}

interface ObjectRowProps {
  object: CustomFieldObject;
  cfg: FormValues['objectConfig'][CustomFieldObject] | undefined;
  onChange: (next: FormValues['objectConfig'][CustomFieldObject]) => void;
}

function ObjectRow({ object, cfg, onChange }: ObjectRowProps) {
  const enabled = cfg?.enabled ?? false;
  return (
    <div className="flex items-center justify-between gap-3">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) =>
            onChange({
              enabled: e.target.checked,
              required: e.target.checked ? (cfg?.required ?? false) : false,
              searchable: e.target.checked ? (cfg?.searchable ?? false) : false,
            })
          }
          className="size-4"
        />
        <span className="font-medium">{CUSTOM_FIELD_OBJECT_LABELS[object]}</span>
      </label>
      {/* "Required" and "Searchable" only mean anything once the object on the
          left is enabled, so they stay disabled until it is — but the disabled
          state is carried by the CONTROL (a greyed box, a not-allowed cursor),
          not by fading the label.

          These two rows used to be wrapped in `opacity-40`, which blended the
          muted ink to roughly #c1c8d1 on the white sheet: a 1.7:1 contrast
          ratio at 12px, well under the 4.5:1 AA needs and genuinely hard to
          read. That matters more here than in a typical disabled control,
          because these two words are precisely what tells someone what ticking
          the object beside them will let them do — they have to be legible
          *before* they become active, or the choice is unexplained.

          So the two states are split by ink instead: Muted (#65758b, 4.7:1 —
          the guide's own disabled colour, §1.3) when off, full Ink when on.
          Both clear AA, and near-black against mid-grey still reads as an
          obvious state change. Hierarchy against the object label is held by
          its `font-medium`, not by washing these out. */}
      <div className="flex items-center gap-4 text-xs">
        <label
          className={cn(
            'flex items-center gap-1.5',
            enabled ? 'text-foreground' : 'cursor-not-allowed text-muted-foreground',
          )}
        >
          <input
            type="checkbox"
            disabled={!enabled}
            checked={cfg?.required ?? false}
            onChange={(e) =>
              onChange({
                enabled: true,
                required: e.target.checked,
                searchable: cfg?.searchable ?? false,
              })
            }
            className="size-3.5 disabled:cursor-not-allowed disabled:opacity-60"
          />
          Required
        </label>
        <label
          className={cn(
            'flex items-center gap-1.5',
            enabled ? 'text-foreground' : 'cursor-not-allowed text-muted-foreground',
          )}
        >
          <input
            type="checkbox"
            disabled={!enabled}
            checked={cfg?.searchable ?? false}
            onChange={(e) =>
              onChange({
                enabled: true,
                required: cfg?.required ?? false,
                searchable: e.target.checked,
              })
            }
            className="size-3.5 disabled:cursor-not-allowed disabled:opacity-60"
          />
          Searchable
        </label>
      </div>
    </div>
  );
}

function needsChoices(t: CustomFieldType): boolean {
  return t === 'select' || t === 'multi_select';
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
    .slice(0, 64);
}

function mergeObjectConfig(stored: CustomField['objectConfig']): FormValues['objectConfig'] {
  const out = { ...emptyObjectConfig };
  for (const h of CUSTOM_FIELD_OBJECTS) {
    const cfg = stored?.[h];
    if (cfg) {
      out[h] = {
        enabled: cfg.enabled ?? false,
        required: cfg.required ?? false,
        searchable: cfg.searchable ?? false,
      };
    }
  }
  return out;
}

/** Drop disabled object entries before sending to the API. Reduces noise in
 *  the audit diff and keeps the JSONB compact. */
function prune(objectConfig: FormValues['objectConfig']): Partial<CustomField['objectConfig']> {
  const out: Partial<CustomField['objectConfig']> = {};
  for (const h of CUSTOM_FIELD_OBJECTS) {
    const cfg = objectConfig[h];
    if (cfg?.enabled) out[h] = cfg;
  }
  return out;
}
